import PropTypes from 'prop-types';
import { useState } from 'react';
// PageHead gives the same kicker → display-title → sub → actions rhythm the list
// views use; Th/Empty/StagePill are the shared table + status atoms. Importing Th
// also loads the canonical `.tbl` styles, so the tab tables match the lists.
import {
  DetailIconButton,
  DetailInfoRows,
} from '@/shared/PanelPrimitives/PanelPrimitives';
import { StatusDotPill } from '@/shared/Ledger/Ledger';
import { Alert, Empty, PageHead, StagePill, Th } from '@/ui';
import { DeleteIcon, DownloadIcon, RefreshIcon } from '@/ui/icons';
import {
  admitsAssignee,
  causaVerificationDisplay,
  isCausaNotFound,
} from './causaDisplay';
import { supportLinks } from './causaContact';
import './CausaDetalleVista.css';

// Causa detail on the shared detail shell (Concepto B), same ladder as the
// diligencia detail: kicker → title + actions → sub → meta band → nav band
// → body (aside 322px left | content right).
//
// Layout: PageHead (kicker `Causa · rol` → partes → `tribunal · corte` sub →
// meta band Competencia · Procedimiento · Estado · Encargado) → `.dnav` with
// the tabs [Resumen · Actuaciones · Diligencias] → `.dbody .cb-cols`:
//   • aside — "Datos de la causa": Etapa · Ingreso · Libro · Ubicación ·
//       Últ. receptor as shared `.dd-info` rows. Always visible, independent
//       of the active tab; empty values render "—", never hidden. Últ.
//       receptor comes from the case's receptorNotifications, whatever the
//       active tab.
//   • main  — the active tab's panel. Resumen = active-diligencia card + the
//       3 most recent actuaciones. Diligencias = the full historial (all
//       tasks on the causa), with the active one highlighted. Actuaciones =
//       `.tbl` records.
const TABS = [
  { id: 'resumen', label: 'Resumen' },
  { id: 'actuaciones', label: 'Actuaciones' },
  { id: 'diligencias', label: 'Diligencias' },
];

// Head-action glyphs come from the shared set (@/ui/icons). RefreshIcon is
// 16-grid (native `.dd-iconbtn` stroke math); the 24-grid Delete / Download
// take explicit size + stroke overrides so the rendered weight stays the same
// (1.4 on a 16 grid ≡ 2.1 on a 24 grid).

// NotFound — the "no-encontrada" UI for a causa. ponytail: inline here; extract
// to @/ui when the diligencia/receptor detail views need the same block.
// The read of the causa failed. Distinct from CausaNotFound, which states that
// the rol belongs to no subscribed causa: that is a fact about the collection,
// and this is a fact about the request. Rendering the first for the second told
// the reader their causa was gone.
export function CausaReadFailed({ onBack, onRetry }) {
  return (
    <main className="page cd-page reveal">
      <div className="detail-notfound">
        <h1 className="page-title">No se pudo cargar la causa</h1>
        <p className="detail-notfound__body">
          La lectura falló. La causa sigue ahí; lo que no llegó es la respuesta.
        </p>
        <button
          type="button"
          className="t-btn t-btn--primary"
          onClick={onRetry}
        >
          Reintentar
        </button>
        <button type="button" className="t-btn t-btn--ghost" onClick={onBack}>
          Volver a Causas
        </button>
      </div>
    </main>
  );
}
CausaReadFailed.propTypes = {
  onBack: PropTypes.func,
  onRetry: PropTypes.func,
};

export function CausaNotFound({ onBack }) {
  return (
    <main className="page cd-page reveal">
      <div className="detail-notfound">
        <h1 className="page-title">Causa no encontrada</h1>
        <p className="detail-notfound__body">
          El rol no corresponde a ninguna causa suscrita por el estudio.
        </p>
        <button type="button" className="t-btn t-btn--ghost" onClick={onBack}>
          Volver a Causas
        </button>
      </div>
    </main>
  );
}

CausaNotFound.propTypes = { onBack: PropTypes.func.isRequired };

// A causa the PJUD does not return is not a dead end: the public lookup does
// not show those under reserve until notification, family cases, or those
// reserved by law or by court order, and it may also be a miss in our search.
// Instead of leaving the lawyer with no way out, the block explains and offers
// to handle it by WhatsApp or email, with the rol and tribunal already filled in.
function causaNotFoundContact(rol, tribunal, whatsappNumber) {
  const where = tribunal && tribunal !== '—' ? ` (${tribunal})` : '';
  return supportLinks({
    text: `Hola, necesito gestionar una diligencia en la causa ${rol}${where}, que aparece como no encontrada en Recepthor.`,
    subject: `Gestionar causa ${rol}`,
    whatsappNumber,
  });
}

function CausaNoEncontradaAviso({ rol, tribunal, whatsappNumber }) {
  const { whatsapp, mailto } = causaNotFoundContact(
    rol,
    tribunal,
    whatsappNumber,
  );
  return (
    <div className="cd-notfound-aviso">
      <Alert tone="warning" title="Esta causa no aparece en el PJUD">
        <p className="cd-notfound-aviso__lead">
          Puede que no aparezca en la consulta pública por alguna de estas
          razones:
        </p>
        <ul className="cd-notfound-aviso__list">
          <li>
            La demanda o la medida aún no se notifica (reservada hasta la
            notificación).
          </li>
          <li>Es una causa de familia.</li>
          <li>Está reservada por ley o por resolución del tribunal.</li>
        </ul>
        <p className="cd-notfound-aviso__lead">
          También puede deberse a un error de nuestra búsqueda. Escríbenos para
          gestionarla.
        </p>
        <div className="cd-notfound-aviso__actions">
          {whatsapp && (
            <a
              className="t-btn t-btn--primary t-btn--sm"
              href={whatsapp}
              target="_blank"
              rel="noopener noreferrer"
            >
              Escribir por WhatsApp
            </a>
          )}
          <a
            className={`t-btn t-btn--sm ${whatsapp ? 't-btn--ghost' : 't-btn--primary'}`}
            href={mailto}
          >
            Escribir por correo
          </a>
        </div>
      </Alert>
    </div>
  );
}

CausaNoEncontradaAviso.propTypes = {
  rol: PropTypes.string.isRequired,
  tribunal: PropTypes.string,
  whatsappNumber: PropTypes.string,
};

// Quiet causa-admin icon actions (sync / dejar de seguir) for the head's action
// slot, kept visually secondary to the primary CTA.
function HeadIcons({ canResync, syncing, onSync, onDelete }) {
  return (
    <>
      {onSync && canResync && (
        <DetailIconButton
          title={
            syncing ? 'Sincronizando…' : 'Sincronizar con el Poder Judicial'
          }
          ariaLabel="Sincronizar causa"
          disabled={syncing}
          onClick={onSync}
        >
          <RefreshIcon className={syncing ? 'cd-sync-spin' : undefined} />
        </DetailIconButton>
      )}
      {onDelete && (
        <DetailIconButton
          title="Dejar de seguir esta causa"
          ariaLabel="Eliminar causa"
          onClick={onDelete}
        >
          <DeleteIcon size={15} style={{ strokeWidth: 2.1 }} />
        </DetailIconButton>
      )}
    </>
  );
}

HeadIcons.propTypes = {
  canResync: PropTypes.bool,
  syncing: PropTypes.bool,
  onSync: PropTypes.func,
  onDelete: PropTypes.func,
};

// Resumen tab: the active-diligencia card (or a "sin diligencia" prompt) +
// the most recent actuaciones as a quick glance into the record.
function ResumenPanel({
  actingsFailed,
  activeDiligencia,
  recentActuaciones,
  onViewDiligencia,
  onNuevaDiligencia,
  nuevaDisabledReason,
  onVerActuaciones,
}) {
  const dilRows = activeDiligencia
    ? [
        ['Receptor', activeDiligencia.receptor],
        ['Plazo', activeDiligencia.plazo],
      ].filter(([, v]) => v && v !== '—')
    : [];

  return (
    <div className="cd-panel">
      <p className="cd-colhead">Estado</p>
      {activeDiligencia ? (
        <div
          className="cd-dilcard"
          role="button"
          tabIndex={0}
          onClick={() => onViewDiligencia?.(activeDiligencia.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onViewDiligencia?.(activeDiligencia.id);
            }
          }}
        >
          <div className="cd-dilcard__top">
            <StagePill
              tone={activeDiligencia.tone}
              label={activeDiligencia.label}
            />
            <span className="cd-dilcard__cta">Ver diligencia →</span>
          </div>
          {dilRows.length > 0 && (
            <div className="cd-dilrows">
              {dilRows.map(([k, v]) => (
                <div className="cd-dilrows__r" key={k}>
                  <span className="cd-k">{k}</span>
                  <span>{v}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="cd-dilempty">
          <p>Sin diligencia activa</p>
          {/* El control no se dibuja sobre una causa que no admite diligencia,
              en vez de dibujarse deshabilitado: el API gatea sobre lo mismo, así
              que ofrecerlo era ofrecer un conflicto. La razón se queda en su
              lugar, porque acá no hay una píldora PJUD que la diga. */}
          {onNuevaDiligencia && !nuevaDisabledReason && (
            <button
              type="button"
              className="t-btn t-btn--primary t-btn--sm"
              onClick={onNuevaDiligencia}
            >
              Nueva diligencia
            </button>
          )}
          {nuevaDisabledReason && <p>{nuevaDisabledReason}</p>}
        </div>
      )}

      <div className="cd-recent__head">
        <p className="cd-colhead">Últimas actuaciones</p>
        {recentActuaciones.length > 0 && onVerActuaciones && (
          <button
            type="button"
            className="card__link"
            onClick={onVerActuaciones}
          >
            Ver todas →
          </button>
        )}
      </div>
      {recentActuaciones.length > 0 ? (
        <div className="cd-recent">
          {recentActuaciones.map((a) => (
            <div className="cd-recent__row" key={a.id}>
              <span className="cd-recent__date num note">{a.fecha}</span>
              <div className="cd-recent__body">
                <span className="tbl__primary">{a.titulo}</span>
                {a.stage && a.stage !== '—' && (
                  <span className="cd-recent__stage">{a.stage}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : actingsFailed ? (
        // A read that failed and a causa the scraper has not filled yet render
        // the same emptiness, and the second is what the sync exists to change.
        <p className="cd-empty-note is-err">
          No se pudieron cargar las actuaciones.
        </p>
      ) : (
        <p className="cd-empty-note">Sin actuaciones registradas.</p>
      )}
    </div>
  );
}

ResumenPanel.propTypes = {
  actingsFailed: PropTypes.bool,
  activeDiligencia: PropTypes.shape({
    id: PropTypes.any,
    tone: PropTypes.string,
    label: PropTypes.node,
    receptor: PropTypes.string,
    plazo: PropTypes.string,
  }),
  recentActuaciones: PropTypes.array,
  onViewDiligencia: PropTypes.func,
  onNuevaDiligencia: PropTypes.func,
  nuevaDisabledReason: PropTypes.string,
  onVerActuaciones: PropTypes.func,
};

ResumenPanel.defaultProps = { recentActuaciones: [] };

// The aside — "Datos de la causa": the causa's remaining legal facts as the
// shared `.dd-info` rows (Competencia / Procedimiento / Estado / Encargado
// moved up to the head's meta band). Fields never hide: empty → "—".
function DatosCausa({ causa }) {
  const rows = [
    ['Etapa', causa.etapa],
    ['Ingreso', causa.fechaIngreso],
    ['Libro', causa.libro],
    ['Ubicación', causa.ubicacion],
    ['Últ. receptor', causa.ultReceptor],
  ];

  return (
    <aside className="cb-aside" aria-label="Datos de la causa">
      <div className="cb-fichahead">
        <p className="cd-colhead">Datos de la causa</p>
      </div>
      <DetailInfoRows rows={rows} />
    </aside>
  );
}

DatosCausa.propTypes = {
  causa: PropTypes.shape({
    etapa: PropTypes.string,
    fechaIngreso: PropTypes.string,
    libro: PropTypes.string,
    ubicacion: PropTypes.string,
    ultReceptor: PropTypes.string,
  }).isRequired,
};

// One tab's table — the canonical `.tbl` (identical to the Causas/Diligencias
// list views), an `Empty` block when there are no rows, and optional row-click.
// Columns carry an optional `render(row)` for the canon cell markup
// (tbl__primary / secondary / icon-link); `rowClassName(row)` tags a row (used
// to highlight the active diligencia in the historial).
function TabTable({
  label,
  columns,
  rows,
  onRowClick,
  canRowClick,
  rowClassName,
}) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return (
      <Empty
        title={`Sin ${label.toLowerCase()}`}
        body={`Esta causa no tiene ${label.toLowerCase()} registradas.`}
      />
    );
  }
  return (
    <div className="tbl-wrap" role="tabpanel" aria-label={label}>
      <table className="tbl">
        <thead>
          <tr>
            {columns.map((c) => (
              <Th key={c.key} w={c.w} num={c.num}>
                {c.label}
              </Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const clickable =
              !!onRowClick && (!canRowClick || canRowClick(row));
            return (
              <tr
                key={row.id}
                className={rowClassName ? rowClassName(row) : undefined}
                onClick={clickable ? () => onRowClick(row) : undefined}
                style={clickable ? { cursor: 'pointer' } : undefined}
              >
                {columns.map((c) => (
                  <td key={c.key} className={c.td}>
                    {c.render ? c.render(row) : (row[c.key] ?? '—')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

TabTable.propTypes = {
  label: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string,
      w: PropTypes.string,
      num: PropTypes.bool,
      td: PropTypes.string,
      render: PropTypes.func,
    }),
  ).isRequired,
  rows: PropTypes.array,
  onRowClick: PropTypes.func,
  canRowClick: PropTypes.func,
  rowClassName: PropTypes.func,
};

// Full-width tab bar — the same `.tabs`/`.tab`/`.tab--on` markup the list views
// (CausasLista) use, so the detail and the lists share one tab treatment.
function CausaTabs({ tabs, counts, activeTab, onTabChange }) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={activeTab === tab.id}
          className={`tab${activeTab === tab.id ? ' tab--on' : ''}`}
          onClick={() => onTabChange(tab.id)}
        >
          {tab.label}
          {counts[tab.id] != null && (
            <span className="tab__n">{counts[tab.id]}</span>
          )}
        </button>
      ))}
    </div>
  );
}

CausaTabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  counts: PropTypes.objectOf(PropTypes.number),
  activeTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
};

CausaTabs.defaultProps = { counts: {} };

export function CausaDetalle({
  actingsFailed,
  causa,
  actuaciones,
  diligencias,
  activeDiligencia,
  tabCounts,
  activeTab: activeTabProp,
  onTabChange,
  onViewDiligencia,
  onViewActuacion,
  onNuevaDiligencia,
  nuevaDisabledReason,
  verificationStatus,
  whatsappNumber,
  encargado,
  onAsignarEncargado,
  canResync,
  syncing,
  onSync,
  onDelete,
}) {
  const [internalTab, setInternalTab] = useState('resumen');
  const activeTab = activeTabProp ?? internalTab;
  const handleTab = (id) => {
    setInternalTab(id);
    onTabChange?.(id);
  };

  const counts = tabCounts ?? {
    actuaciones: Array.isArray(actuaciones) ? actuaciones.length : 0,
    diligencias: Array.isArray(diligencias) ? diligencias.length : 0,
  };

  // Court sub-line: tribunal · corte (drop blanks / em-dashes). Estado lives
  // in the meta band, not here — same sub shape as the diligencia detail.
  const sub = [causa.tribunal, causa.corte]
    .filter((v) => v && v !== '—')
    .join(' · ');

  // Meta band (detail shell): the causa's classifying facts + the encargado.
  // PageHead renders empty values as "—" (fields never hide). When the caller
  // may assign (onAsignarEncargado present — permission-gated upstream), the
  // encargado value is the asignar/cambiar affordance.
  // PJUD = scrape verification state (Sincronizando/Suscrita/No encontrada) —
  // visible here because it gates "Nueva diligencia" (nuevaDisabledReason).
  const verif = verificationStatus
    ? causaVerificationDisplay(verificationStatus)
    : null;
  const VERIF_PILL_STATUS = { sync: 'pay', ok: 'ok', err: 'err' };
  const meta = [
    { k: 'Competencia', v: causa.competencia },
    { k: 'Procedimiento', v: causa.procedimiento },
    { k: 'Estado', v: causa.estado },
    ...(verif
      ? [
          {
            k: 'PJUD',
            v: (
              <StatusDotPill
                status={VERIF_PILL_STATUS[verif.dot] ?? 'info'}
                label={verif.label}
              />
            ),
          },
        ]
      : []),
    ...(admitsAssignee(verificationStatus)
      ? [{ k: 'Encargado', v: encargado, onClick: onAsignarEncargado }]
      : []),
  ];

  // Primary CTA lives in the head only when there is no active diligencia
  // (otherwise the Resumen card is the CTA). Quiet admin icons always show.
  //
  // Una causa que el PJUD no confirmó no admite diligencia, y el API gatea
  // sobre lo mismo. Antes el control se dibujaba deshabilitado con su razón al
  // lado; ahora no se dibuja. El porqué no se pierde: la píldora PJUD de esta
  // misma cabecera dice en qué estado está la causa, que es la causa raíz, y un
  // control deshabilitado que repite esa razón es una segunda voz para el mismo
  // hecho.
  const showNueva =
    !activeDiligencia && !!onNuevaDiligencia && !nuevaDisabledReason;
  const hasHeadActions = showNueva || (onSync && canResync) || onDelete;
  const headActions = hasHeadActions ? (
    <>
      <HeadIcons
        canResync={canResync}
        syncing={syncing}
        onSync={onSync}
        onDelete={onDelete}
      />
      {showNueva && (
        <button
          type="button"
          className="t-btn t-btn--primary"
          onClick={onNuevaDiligencia}
          disabled={Boolean(nuevaDisabledReason)}
          title={nuevaDisabledReason || undefined}
        >
          Nueva diligencia
        </button>
      )}
    </>
  ) : undefined;

  // Canon cell markup (tbl__primary / secondary / num note / icon-link).
  const actuacionCols = [
    { key: 'fecha', label: 'Fecha', w: '20%', td: 'num note' },
    {
      key: 'titulo',
      label: 'Actuación',
      render: (a) => (
        <>
          <div className="tbl__primary" style={{ fontWeight: 400 }}>
            {a.titulo}
          </div>
          {a.stage && <div className="tbl__secondary">{a.stage}</div>}
        </>
      ),
    },
    {
      key: 'pdf',
      w: '44px',
      td: 'num',
      render: (a) =>
        a.fileUrl && onViewActuacion ? (
          <button
            type="button"
            className="cd-iconlink"
            title="Ver documento"
            aria-label="Ver documento"
            onClick={() => onViewActuacion(a)}
          >
            <DownloadIcon size={16} strokeWidth={2.1} />
          </button>
        ) : null,
    },
  ];
  const diligCols = [
    { key: 'fecha', label: 'Fecha', w: '20%', td: 'note' },
    { key: 'estado', label: 'Estado' },
    { key: 'receptor', label: 'Receptor', w: '32%' },
  ];

  const recentActuaciones = Array.isArray(actuaciones)
    ? actuaciones.slice(0, 3)
    : [];
  const activeId = activeDiligencia?.id;

  return (
    <main className="page cd-page reveal">
      <PageHead
        kicker={`Causa · ${causa.rol}`}
        title={causa.partes}
        sub={sub || undefined}
        action={headActions}
        meta={meta}
      />

      {isCausaNotFound(verificationStatus) && (
        <CausaNoEncontradaAviso
          rol={causa.rol}
          tribunal={causa.tribunal}
          whatsappNumber={whatsappNumber}
        />
      )}

      <div className="dnav">
        <CausaTabs
          tabs={TABS}
          counts={counts}
          activeTab={activeTab}
          onTabChange={handleTab}
        />
      </div>

      <div className="dbody cb-cols">
        <DatosCausa causa={causa} />

        <div className="cd-main">
          {activeTab === 'resumen' && (
            <ResumenPanel
              actingsFailed={actingsFailed}
              activeDiligencia={activeDiligencia}
              recentActuaciones={recentActuaciones}
              onViewDiligencia={onViewDiligencia}
              onNuevaDiligencia={onNuevaDiligencia}
              nuevaDisabledReason={nuevaDisabledReason}
              onVerActuaciones={() => handleTab('actuaciones')}
            />
          )}
          {activeTab === 'actuaciones' && (
            <TabTable
              label="Actuaciones"
              columns={actuacionCols}
              rows={actuaciones}
            />
          )}
          {activeTab === 'diligencias' && (
            <TabTable
              label="Diligencias"
              columns={diligCols}
              rows={diligencias}
              onRowClick={
                onViewDiligencia ? (row) => onViewDiligencia(row.id) : undefined
              }
              rowClassName={(row) =>
                activeId != null && row.id === activeId
                  ? 'cd-row-active'
                  : undefined
              }
            />
          )}
        </div>
      </div>
    </main>
  );
}

CausaDetalle.propTypes = {
  actingsFailed: PropTypes.bool,
  causa: PropTypes.shape({
    rol: PropTypes.string.isRequired,
    tribunal: PropTypes.string.isRequired,
    corte: PropTypes.string,
    partes: PropTypes.string.isRequired,
    competencia: PropTypes.string,
    estado: PropTypes.string,
    etapa: PropTypes.string,
    procedimiento: PropTypes.string,
    libro: PropTypes.string,
    ubicacion: PropTypes.string,
    fechaIngreso: PropTypes.string,
    ultReceptor: PropTypes.string,
  }).isRequired,
  actuaciones: PropTypes.array,
  diligencias: PropTypes.array,
  activeDiligencia: PropTypes.shape({
    id: PropTypes.any,
    tone: PropTypes.string,
    label: PropTypes.node,
    receptor: PropTypes.string,
    plazo: PropTypes.string,
  }),
  tabCounts: PropTypes.objectOf(PropTypes.number),
  activeTab: PropTypes.string,
  onTabChange: PropTypes.func,
  onViewDiligencia: PropTypes.func,
  onViewActuacion: PropTypes.func,
  onNuevaDiligencia: PropTypes.func,
  nuevaDisabledReason: PropTypes.string,
  verificationStatus: PropTypes.string,
  whatsappNumber: PropTypes.string,
  encargado: PropTypes.string,
  onAsignarEncargado: PropTypes.func,
  canResync: PropTypes.bool,
  syncing: PropTypes.bool,
  onSync: PropTypes.func,
  onDelete: PropTypes.func,
};

export default CausaDetalle;
